import store from '@/vuex/store';
import { v1 as uuid } from 'uuid';
import sha256 from 'js-sha256';

//  ================================
//  = Google Tag Manager =
//  ================================
//

// Tracks when unfocusing on a form fields on Login, Register, Forgotten Password, Reset Password & Brochure Request
export const formProgression = (formData) => {
  const targetElement = formData.target;

  // Check if the target element is an HTML element and has a closest method
  if (targetElement instanceof HTMLElement && typeof targetElement.closest === 'function') {
    const formElement = targetElement.closest('form');

    // If the form element exists and has a name, push form progression data to dataLayer
    if (formElement && formElement.name) {
      const formName = formElement.name;
      dataLayer.push({
        event: 'formProgression',
        formName,
        fieldLabel: targetElement.name
      });
    }
  }
};

// Tracks general events with ecommerce data
export const event = (event, ecommerce) => {
  dataLayer.push({
    event: event,
    ecommerce: ecommerce
  });
};

// Formats a string by converting it to lowercase and removing all spaces.
export const formatZiCode = (str) => {
  return str.toLowerCase().replace(/\s+/g, '');
};

// Tracks when brochure form has been submitted
export const brochureRequestPost = (id, customer) => {
  dataLayer.push({
    event: 'brochure_request_post',
    id: uuid(),
    _clear: true,
    user: {
      city: customer.city.toLowerCase(),
      country: customer.country.toLowerCase(),
      email: customer.email.toLowerCase(),
      email_sha256: sha256(customer.email),
      first_name: customer.firstName.toLowerCase(),
      last_name: customer.lastName.toLowerCase(),
      phone: customer.phone,
      zipcode: formatZiCode(customer.postal)
    }
  });
};

// Tracks when brochure view online link has been clicked
export const brochureRequestEmail = (id, customer) => {
  dataLayer.push({
    event: 'brochure_request_email',
    id: uuid(),
    _clear: true,
    user: {
      email: customer.email.toLowerCase(),
      email_sha256: sha256(customer.email),
      first_name: customer.firstName.toLowerCase(),
      last_name: customer.lastName.toLowerCase()
    }
  });
};

// Tracks when tour sticky navigation item has been clicked
export const holidayAnchor = (holidayName, anchorType) => {
  dataLayer.push({
    event: 'holidayAnchor',
    holidayName,
    anchorType
  });
};

// Tracks when a holiday search has been submitted
export const searchEvent = (searchWhere, searchWhen, searchHow, searchDepart) => {
  dataLayer.push({
    event: 'searchEvent',
    searchFilter: `Destination: ${searchWhere}, Departing Date: ${searchWhen}, Travel Type: ${searchHow}, Departure: ${searchDepart}`
  });
};

// Formats dates from 'Aug 2024' to '2024-08'
const formatDates = (dates) => {
  return dates.map(date => {
    let [month, year] = date.split(' ');
    month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    const formattedMonth = monthMapping[month];
    return `${year}-${formattedMonth}`;
  });
}

// Tracks when a holiday search has been submitted
export const searchEventNew = (searchWhere, searchWhen, searchHow, searchDepart) => {
  const sortedDeparture = Array.isArray(searchDepart) ? searchDepart.map(value => value.toLowerCase()).sort() : [];
  const departureMonths = formatDates(searchWhen);
  const holidayTypes = Array.isArray(searchHow) ? searchHow.map(type => type.toLowerCase()).sort() : [];

  const filters = {};

  if (sortedDeparture.length > 0) {
    filters.departure_airport = sortedDeparture;
  }
  if (departureMonths.length > 0) {
    filters.departure_month = departureMonths;
  }
  if (holidayTypes.length > 0) {
    filters.holiday_type = holidayTypes;
  }
  
  dataLayer.push({
    event: 'search',
    id: uuid(),
    _clear: true,
    event_data: {
      search_term: searchWhere
    },
    filters: filters
  });
};

// Helper function to get selected values from a filter
const getSelectedValues = (itemsInfo, filterKey) => {
  const filter = itemsInfo.filters.find(filter => filter.value === filterKey);

  if (filter && filter.children) {
    const selectedItems = filter.children.filter(item => item.selected);
    const selectedLabels = selectedItems.map(item => item.label.toLowerCase());

    return selectedLabels;
  } else {
    return [];
  }
}

// Helper function to get min and max values from a filter
const getFilterValue = (itemsInfo, filterKey) => {
  const filter = itemsInfo.filters.find(filter => filter.value === filterKey);
  let min = null;
  let max = null;
  filter.children.forEach(item => {
    if (item.selected) {
      if (min === null || item.from < min) {
        min = item.from;
      }
      if (max === null || item.to > max) {
        max = item.to;
      }
    }
  });
  return { min, max };
}

const monthMapping = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12'
};

// Adds a filter to the filters object if the value is valid
const addFilterIfValid = (filters, key, value) => {
  if (value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
    filters[key] = value;
  }
};

// Tracks item list on search results page
export const viewItemList = (itemsInfo) => {
  const priceValues = getFilterValue(itemsInfo, 'Prices');
  const durationValues = getFilterValue(itemsInfo, 'Durations');

  let filters = {};
  
  addFilterIfValid(filters, 'continents', getSelectedValues(itemsInfo, 'Continents'));
  addFilterIfValid(filters, 'countries', getSelectedValues(itemsInfo, 'Countries'));
  addFilterIfValid(filters, 'departure_airport', getSelectedValues(itemsInfo, 'Departs'));
  addFilterIfValid(filters, 'departure_month', formatDates(getSelectedValues(itemsInfo, 'Dates')));
  addFilterIfValid(filters, 'duration_days_max', durationValues.max);
  addFilterIfValid(filters, 'duration_days_min', durationValues.min);
  addFilterIfValid(filters, 'holiday_type', getSelectedValues(itemsInfo, 'Types'));
  addFilterIfValid(filters, 'price_max', priceValues.max);
  addFilterIfValid(filters, 'price_min', priceValues.min);

  const itemsArray = itemsInfo.items.map((item, index) => {
    const itemObject = {
      item_brand: item.brand,
      item_category: 'tours',
      discount: item.discount,
      index: index + 1,
      item_id: item.tourCode,
      item_list_name: 'search results',
      item_name: item.name,
      price: item.tourSummary.fromPrice
    };

    if (item.continent) {
      itemObject.continent = item.continent;
      itemObject.item_category2 = item.continent;
    }

    if (item.country) {
      itemObject.country = item.country;
      itemObject.item_category3 = item.country;
    }

    return itemObject;
  });

  dataLayer.push({
    event: 'view_item_list',
    id: uuid(),
    _clear: true,
    event_data: {
      currency: itemsInfo.items[0].tourSummary.currency.toLowerCase(),
      search_results_count: itemsInfo.itemsCount
    },
    filters: filters,
    items: itemsArray
  });
}

// Tracks when login form has been submitted
export const loginEvent = (userId, userEmail) => {
  const userData = {
    email: userEmail.toLowerCase(),
    email_sha256: sha256(userEmail)
  };

  if (userId) {
    userData.id = userId;
  }

  dataLayer.push({
    event: 'login',
    id: uuid(),
    _clear: true,
    user: userData
  });
};
 
// Tracks when registration form has been submitted
export const registrationEvent = (userId, userEmail) => {
  dataLayer.push({
    event: 'sign_up',
    id: uuid(),
    _clear: true,
    user: {
      email: userEmail.toLowerCase(),
      email_sha256: sha256(userEmail)
      // id: userId
    },
  });
};
 
// Tracks on form submit for: Forgotten Password & Reset Password
export const formSubmitEvent = (formName) => {
  dataLayer.push({
    event: 'formSubmit',
    formName: formName.toLowerCase()
  });
};

// Tracks newsletter subscribe
export const newsletterSubscribe = (customer, isBrochure = false) => {
  dataLayer.push({
    event: 'newsletter_subscribe',
    id: uuid(),
    _clear: true,
    event_data: {
      method: isBrochure ? 'brochure_request' : 'checkout'
    },
    user: {
      country: customer.address.fields.country.value.toLowerCase(),
      email: customer.fields.email.value.toLowerCase(),
      email_sha256: sha256(customer.fields.email.value),
      first_name: customer.firstName.toLowerCase(),
      last_name: customer.lastName.toLowerCase()
    },
  });
};

// Formats the date of birth (DOB) in the format 'YYYYMMDD'.
// It ensures that the month and day values are always two digits by padding them with leading zeros if necessary.
export const formatDob = (year, month, day) => {
  return `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
};

// Tracks on page view
export const pageViewEvent = (notParsedPageData, userData) => {
  const pageData = JSON.parse(notParsedPageData);

  const page = {
    category: pageData.category.toLowerCase(),
    continent: pageData.continent.toLowerCase(),
    country: pageData.country.toLowerCase(),
    name: pageData.name.toLowerCase(),
    psuedo_path: pageData.pseudoPath
  };

  Object.keys(page).forEach(key => {
    if (page[key] === '' || page[key] === undefined) {
      delete page[key];
    }
  });

  const user = userData ? {
    city: userData.address.town.toLowerCase(),
    country: userData.address.country.toLowerCase(),
    dob: formatDob(userData.yearOfBirth, userData.monthOfBirth, userData.dayOfBirth),
    email: userData.email ? userData.email.toLowerCase() : '',
    email_sha256: userData.email ? sha256(userData.email).toString() : '',
    first_name: userData.firstName.toLowerCase(),
    id: userData.clientId,
    last_name: userData.lastName.toLowerCase(),
    phone: userData.mobilePhone,
    zipcode: formatZiCode(userData.address.postCode),
    total_orders: userData.totalOrders
  } : {};

  Object.keys(user).forEach(key => {
    if (user[key] === '' || user[key] === undefined || user[key] === null) {
      delete user[key];
    }
  });

  const dataLayerObject = {
    event: 'view_page',
    id: uuid(),
    _clear: true,
    page: page
  };

  if (Object.keys(user).length > 0) {
    dataLayerObject.user = user;
  }

  dataLayer.push(dataLayerObject);
};

// User encounters an error during checkout
export const checkoutError = (errorTitle) => {
  dataLayer.push({
    event: 'checkout_error',
    id: uuid(),
    _clear: true,
    error: {
      message: errorTitle
    }
  });
};

// Maps passenger details to a user object for dataLayer
const mapUser = (passenger, totalOrders = '') => {
  const user = {
    city: passenger.address.fields.town.value.toLowerCase(),
    country: passenger.address.fields.country.value.toLowerCase(),
    dob: formatDob(passenger.fields.yearOfBirth.value, passenger.fields.monthOfBirth.value, passenger.fields.dayOfBirth.value),
    email: passenger.fields.email.value.toLowerCase(),
    email_sha256: sha256(passenger.fields.email.value),
    first_name: passenger.firstName.toLowerCase(),
    id: passenger.passengerId,
    last_name: passenger.lastName.toLowerCase(),
    phone: passenger.fields.mobilePhone.value,
    zipcode: formatZiCode(passenger.address.fields.postCode.value)
  };

  // If totalOrders is provided, add it to the user object
  if (totalOrders !== '') {
    user.total_orders = totalOrders;
  }

  return user;
};

// Calculates the duration of nights between two date-times
const getNightsDuration = (departureDateTime, returnDateTime) => {
  const departure = new Date(departureDateTime);
  const returnD = new Date(returnDateTime);
  const diffTime = returnD - departure;
  const diffDays = diffTime / (1000 * 60 * 60 * 24); 
  return Math.floor(diffDays);
};

// Extracts and formats departure details from the departure object
const extractDepartureDetails = (departure) => {
  const departureLeg = departure.departureLegs.length > 0 ? departure.departureLegs[0] : null;
  const returnLeg = departure.returnLegs.length > 0 ? departure.returnLegs[0] : null;

  let departureDateTime, returnDateTime;

  if (departureLeg && returnLeg) {
    departureDateTime = departureLeg.departureTime.dateTime;
    returnDateTime = returnLeg.departureTime.dateTime;
  } else {
    departureDateTime = departure.departureNoFlights.date;
    returnDateTime = departure.returnNoFlights.date;
  }

  const nightsDuration = getNightsDuration(departureDateTime, returnDateTime);
  const departureDateObj = new Date(departureDateTime);
  const departureYear = departureDateObj.getFullYear();
  const departureMonth = departureDateObj.getMonth() + 1;
  const departureDate = departureDateObj.toISOString().split('T')[0];

  const result = {
    departure_date: departureDate,
    departure_month: `${departureYear}-${departureMonth < 10 ? '0' + departureMonth : departureMonth}`,
    nights_duration: nightsDuration
  };

  if (departureLeg && returnLeg) {
    result.airline = departureLeg.flightCarrier.name.toLowerCase();
    result.departure_airport = departureLeg.departureAirport.name.toLowerCase();
    result.departure_id = departureLeg.departureId;
  }

  return result;
};

// Formats a price to return its numeric value.
export const formatPrice = (price) => {
  if (typeof price === 'string') {
    // Remove non-numeric characters and convert to number
    return parseInt(price.replace(/[^0-9]/g, ''), 10);
  }
  return price;
};

// Adds items to the items array with a specified category
const addItems = (items, category) => {
  if (!items || !items.length) return [];
  return items.map((item) => {
    let newItem = { item_category: category };

    // Set item_id based on the available property (tourCode or id)
    if (item.tourCode) {
      newItem.item_id = item.tourCode;
    } else if (item.id) {
      newItem.item_id = item.id;
    }

    // Set item_name if available
    if (item.name) newItem.item_name = item.name.trim();

    // Copy selected fields from item to newItem
    ['brand', 'continent', 'country', 'discount', 'price', 'quantity', 'variant'].forEach(field => {
      if (item[field]) {
        newItem[field] = item[field];
      }
    });

    return newItem;
  });
};

// Maps item details to a transaction item object for dataLayer
const mapItemToTransactionItem = (item, brand) => {
  let mappedItem = {
    item_category: item.category
  };

  // Determine the brand based on category
  if (item.item_category === 'flights') {
    mappedItem.item_brand = item.brand.toLowerCase();
  } else {
    mappedItem.item_brand = (brand || item.brand || '').toLowerCase();
  }

  // Map basic item properties to the mappedItem object
  if (item.item_id) mappedItem.item_id = item.item_id;
  if (item.item_name) mappedItem.item_name = item.item_name.toLowerCase();
  if (item.continent) mappedItem.item_category2 = item.continent;
  if (item.country) mappedItem.item_category3 = item.country;

  // Set price to 0 for flights
  if (item.item_category === 'flights') {
    mappedItem.price = 0;
  }

  // Map other item properties to the mappedItem object
  Object.keys(item).forEach(key => {
    if (item[key] && key !== 'category' && key !== 'item_id' && key !== 'item_name' && key !== 'brand') {
      if (key === 'variant') {
        mappedItem[key] = item[key].toLowerCase();
      } else if (key === 'price') {
        mappedItem[key] = formatPrice(item[key]);
      } else {
        mappedItem[key] = item[key];
      }
    }
  });

  return mappedItem;
};

// Get number of rooms
export const getRoomNumbers = () => {
  return store.state.checkout.rooms.length;
};

// Get payment type from store
export const getPaymentType = () => {
  return store.state.checkout.paymentType;
};
 
// Get transaction type from store
export const getTransactionType = () => {
  return store.state.checkout.transactionType;
};

// Track view item(tour page)
export const viewItem = (dataLayerItem) => {
  let itemsList = [];

  if (dataLayerItem) {
    itemsList = itemsList.concat(addItems([dataLayerItem], 'tours'));
  }

  const brand = dataLayerItem.brand;

  dataLayer.push({
    event: 'view_item',
    id: uuid(),
    _clear: true,
    event_data: {
      currency: dataLayerItem.currency,
      nights_duration: dataLayerItem.nights,
      tour_code: dataLayerItem.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Checkout flow starting here

// Track add to cart
export const addToCart = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.departure) {
    itemsList = itemsList.concat(addItems([dataLayerItem.departure.destinationDetails], 'tours'));
  }

  const brand = dataLayerItem.brand.toLowerCase();

  if(dataLayerItem.departure.details) {
    departureDetails = extractDepartureDetails(dataLayerItem.departure.details);
  }

  // Build tracking object conditionally
  const tracking = {};
  if (dataLayerItem.utmParams.utmSource) {
    tracking.source = dataLayerItem.utmParams.utmSource;
  }
  if (dataLayerItem.utmParams.utmMedium) {
    tracking.subsource = dataLayerItem.utmParams.utmMedium;
  }

  const dataLayerObj = {
    event: 'add_to_cart',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.currency.toLowerCase(),
      tour_code: dataLayerItem.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand))
  };

  // Add tracking object only if it has values
  if (Object.keys(tracking).length > 0) {
    dataLayerObj.tracking = tracking;
  }

  dataLayer.push(dataLayerObj);
}

// Track add tour extension
export const addTourExtension = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.addons, 'addons'));
  }

  const brand = dataLayerItem.data.brand.toLowerCase();

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_tour_extension',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add begin checkout
export const beginCheckout = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.tours, 'tours'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'begin_checkout',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add package
export const addPackage = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.rooms) {
    itemsList = itemsList.concat(addItems(dataLayerItem.rooms, 'rooms'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_package',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add Excursions
export const addExcursions = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.excursions, 'excursions'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_excursions',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add shipping info
export const addShippingInfo = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.data.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.tours, 'tours'));
  }
  if (dataLayerItem && dataLayerItem.data.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.excursions, 'excursions'));
  }
  if (dataLayerItem && dataLayerItem.data.flights) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.flights, 'flights'));
  }
  if (dataLayerItem && dataLayerItem.data.donations) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.donations, 'donations'));
  }
  if (dataLayerItem && dataLayerItem.data.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.addons, 'addons'));
  }

  const passenger = dataLayerItem.data.passengers[0];
  const brand = dataLayerItem.brand;

  if(dataLayerItem.data.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.data.departureDetails);
  }

  dataLayer.push({
    event: 'add_shipping_info',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: dataLayerItem.quote.tourCode,
      travellers: dataLayerItem.quote.passengers.length,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      value: dataLayerItem.quote.subtotal
    },
    user: mapUser(passenger)
  });
};

// Track add payment info
export const addPaymentInfo = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.data.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.tours, 'tours'));
  }
  if (dataLayerItem && dataLayerItem.data.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.excursions, 'excursions'));
  }
  if (dataLayerItem && dataLayerItem.data.flights) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.flights, 'flights'));
  }
  if (dataLayerItem && dataLayerItem.data.donations) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.donations, 'donations'));
  }
  if (dataLayerItem && dataLayerItem.data.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.addons, 'addons'));
  }

  const passenger = dataLayerItem.data.passengers[0];
  const brand = dataLayerItem.brand;

  if(dataLayerItem.data.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.data.departureDetails);
  }

  dataLayer.push({
    event: 'add_payment_info',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: dataLayerItem.quote.tourCode,
      travellers: dataLayerItem.quote.passengers.length,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      payment_type: dataLayerItem.payment_type,
      transaction_type: dataLayerItem.transaction_type,
      value: dataLayerItem.paymentData.amountToPay
    },
    user: mapUser(passenger)
  });
};

// User attempts to make a purchase
export const bookingPurchaseSubmit = (bookingData) => {
  let itemsList = [];
  let departureDetails = {};

  if (bookingData && bookingData.data.tours) {
    itemsList = itemsList.concat(addItems(bookingData.data.tours, 'tours'));
  }
  if (bookingData && bookingData.data.excursions) {
    itemsList = itemsList.concat(addItems(bookingData.data.excursions, 'excursions'));
  }
  if (bookingData && bookingData.data.flights) {
    itemsList = itemsList.concat(addItems(bookingData.data.flights, 'flights'));
  }
  if (bookingData && bookingData.data.donations) {
    itemsList = itemsList.concat(addItems(bookingData.data.donations, 'donations'));
  }
  if (bookingData && bookingData.data.addons) {
    itemsList = itemsList.concat(addItems(bookingData.data.addons, 'addons'));
  }

  const passenger = bookingData.passengers[0];
  const brand = bookingData.brand;

  if(bookingData.data.departureDetails) {
    departureDetails = extractDepartureDetails(bookingData.data.departureDetails);
  }
  
  // Push the completed transaction
  dataLayer.push({
    event: 'purchase_submit',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: bookingData.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: bookingData.quote.tourCode,
      travellers: bookingData.quote.passengers.length
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      paid_today: bookingData.payment.data.depositAmount,
      payment_type: getPaymentType(),
      transaction_type: getTransactionType(),
      value: bookingData.payment.data.fullBalance
    },
    user: mapUser(passenger)
  });
};

// User completes a successful purchase 
export const bookingPurchase = (bookingData) => {
  let itemsList = [];
  let departureDetails = {};
  let totalOrders = 0;

  if (bookingData && bookingData.tours) {
    itemsList = itemsList.concat(addItems(bookingData.tours, 'tours'));
  }
  if (bookingData && bookingData.excursions) {
    itemsList = itemsList.concat(addItems(bookingData.excursions, 'excursions'));
  }
  if (bookingData && bookingData.flights) {
    itemsList = itemsList.concat(addItems(bookingData.flights, 'flights'));
  }
  if (bookingData && bookingData.donations) {
    itemsList = itemsList.concat(addItems(bookingData.donations, 'donations'));
  }
  if (bookingData && bookingData.addons) {
    itemsList = itemsList.concat(addItems(bookingData.addons, 'addons'));
  }

  const passenger = bookingData.passengers[0];
  const brand = bookingData.data.brand;

  if (passenger.totalOrders !== undefined) {
    totalOrders = passenger.totalOrders;
  }

  if(bookingData.departureDetails) {
    departureDetails = extractDepartureDetails(bookingData.departureDetails);
  }

  // Push the completed transaction
  dataLayer.push({
    event: 'purchase',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: bookingData.booking.currency.toLowerCase(),
      rooms: bookingData.rooms.length,
      tour_code: bookingData.quote.tourCode,
      travellers: bookingData.booking.passengers.length
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      id: bookingData.bookingReference,
      paid_today: bookingData.booking.amountPaid,
      payment_type: getPaymentType(),
      transaction_type: getTransactionType(),
      value: bookingData.booking.total
    },
    user: mapUser(passenger, totalOrders)
  });
};
 
export default {
  setupVuexSubscribe() {
    store.subscribe((mutation) => {
      switch (mutation.type) {
      case 'validateField':
        return formProgression(mutation.payload);
      }
    });
  }
};

const eventClick = (eventData) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);

  requestAnimationFrame(() => {
    isClickHandled = false;
  });
}

document.addEventListener('click', function (event) {
  let isClickHandled = false;

  const link = event.target.closest('.hero-header .image-link-overlay');

  if (link) {
    if (isClickHandled) return;

    isClickHandled = true;

    const slideName = link.getAttribute('data-slide-name')?.toLowerCase() || '';
    const slideLink = link.getAttribute('data-slide-link');
    const slidePosition = parseInt(link.getAttribute('data-slide-position')) + 1;
    const slideUuid = link.getAttribute('data-slide-uuid');

    const eventData = {
      event: 'carousel_hero_header',
      id: slideUuid,
      _clear: true,
      event_data: {
        brand: '@brand',
        page: '@pageName',
        link: slideLink,
        slide_position: slidePosition,
        ...(slideName && { name: slideName })
      },
    };
    eventClick(eventData);
  }
});

document.addEventListener('DOMContentLoaded', function () {
  let isClickHandled = false;

  document.addEventListener('click', function (event) {
    const clickable = event.target.closest('.hero-header .isClickable');

    if (!clickable || isClickHandled) return;

    isClickHandled = true;

    event.preventDefault();
    event.stopPropagation();

    const eventData = {
      event: 'banner_hero_header',
      id: '@heroHeaderUuid',
      _clear: true,
      event_data: {
        brand: '@brand',
        page: '@pageName',
        link: '@Model.Content.BannerLink.Url',
        ...('@bannerLinkName' && { name: '@bannerLinkName' }),
      },
    };

    eventClick(eventData);
  });
});